import {ChangeDetectionStrategy, Component, DestroyRef, Injectable, input, ViewChild} from "@angular/core";
import {Tidssone} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {combineLatestWith, Observable, startWith, Subject, switchMap, tap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {MatFormFieldControl} from "@angular/material/form-field";
import {MatFormFieldControlProxy} from "../../../material/MatFormFieldControlProxy";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {map} from "rxjs/operators";
import {AsyncPipe} from "@angular/common";
import {BrukerDatatjeneste} from "felles-ng2/tjenester/datalager/BrukerDatatjeneste";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {leggTilFeilDersom} from "felles-ng2/infrastruktur/angular/reactiveForm";

const debug = initDebugger('TidssoneVelgerKomponent');

/**
 * Skjemakomponent for angivelse av tidssone som lar bruker velge fra en filtrert nedtrekksmeny.
 */
@Component({
    selector: 'tidssone-velger',
    templateUrl: 'tidssone-velger.html',
    styleUrl: 'tidssone-velger.scss',
    providers: [{
        provide: MatFormFieldControl,
        useFactory: (komponent: TidssoneVelgerKomponent) => new MatFormFieldControlProxy(() => komponent.matInputfelt, komponent.stateChanges),
        deps: [TidssoneVelgerKomponent]
    }],
    imports: [
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatInput,
        AsyncPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TidssoneVelgerKomponent {

    @ViewChild(MatInput) private matInputfelt!: MatInput;

    skjemakontroll = input.required<FormControl<Tidssone>>();
    placeholder = input('');

    standardPlaceholder: string;
    filtrerteTidssoner!: Observable<Tidssone[]>;

    private tidssoner: string[] = [];

    private readonly stateChanges = new Subject<void>();

    constructor(private avhengigheter: TidssoneVelgerKomponentAvhengigheter, private destroyRef: DestroyRef, private oversettelser: Oversettelser) {
        this.standardPlaceholder = this.oversettelser.oversett(i18n('Velg tidssone'));

        this.filtrerteTidssoner = toObservable(this.skjemakontroll)
          .pipe(
            //ved hvert tastetrykk
            switchMap(skjema => skjema.valueChanges),
            //sørger for å levere en tom streng som initiell verdi,
            // slik at filtrerteTidssoner blir populert med den fulle tidssone-listen initielt
            //dermed vises dropdown med alle tilgjengelige tidssoner når bruker klikker inn i feltet
            startWith(''),
            //kombiner inntastet tekst med tilgjengelige filter
            combineLatestWith(this.avhengigheter.tilgjengeligeTidssoner()),
            tap(([filter, tidssoner]) => this.valider(filter, tidssoner)),
            // filterer tilgjengelige tidssoner iht inntastet streng
            map(([filter, tidssoner]) => this.filtrer(filter, tidssoner))
          );
    }

    ngAfterViewInit(): void {
        this.matInputfelt.stateChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.stateChanges.next());

        //uten denne flyter ikke label opp når feltet har en initiell verdi - burde ikke vært nødvendig men har ikke funnet årsaken.. (kai)
        setTimeout(() => this.stateChanges.next(), 0)
    }

    //hjelpemetoder

    private filtrer(filter: string, tidssoner: Tidssone[]): Tidssone[] {
        let filtrerteTidssoner = tidssoner.filter(kandidat => kandidat.toLowerCase().includes(filter.toLowerCase()));
        //debug(`filterer med ${filter}`, filtrerteTidssoner, tidssoner)
        return filtrerteTidssoner;
    }

    /** Kalles ved endringer i tidssone-felt.
     * Validerer at angitt tidssone er blandt tilgjengelige tidssoner, eller er tom.
     * Merk at det kommer en 'NG0100: ExpressionChangedAfterItHasBeenCheckedError' feil når angular kjører i developer mode
     * nå valideringen endrer returverdi, har ikke funnet årsaken til dette, men funk ser ut til å være intakt (kai) */
    private valider(angittTidssone: Tidssone, tidssoner: Tidssone[]) {
        let gyldig = erTomEllerNil(angittTidssone) || tidssoner.includes(angittTidssone);
        leggTilFeilDersom(!gyldig, 'ugyldig', this.skjemakontroll());
        debug(`tidssoneValidator verdi:'${angittTidssone}' ${gyldig ? 'er' : 'er IKKE'} gyldig`)
        this.stateChanges.next()
    }

}

@Injectable()
export class TidssoneVelgerKomponentAvhengigheter {

    constructor(private brukerDatatjeneste: BrukerDatatjeneste) {
    }

    tilgjengeligeTidssoner(): Observable<Tidssone[]> {
        return this.brukerDatatjeneste.tilgjengeligeTidssoner();
    }
}
